import { Observable, AsyncSubject } from 'common/barrels/rx';
import { IEventAggregator, EventAggregator, IDisposable, disposableSubscription } from 'common/barrels/core';
import { IResourceProvider } from 'common/resourceprovider';

import { Busy } from 'runtime/events/busy';
import { NotBusy } from 'runtime/events/notbusy';
import { DialogRequested } from 'runtime/events/dialogrequested';
import { SystemErrorMessage } from 'runtime/events/systemerrormessage';
import { RuntimeHostClosed } from 'runtime/events/runtimehostclosed';
import { RuntimeInfoRequested } from 'runtime/events/runtimeinforequested';
import { ErrorReport } from 'runtime/errorreport';

import { OpenWritableWorkItemRequested } from 'runtime/events/openwritableworkitemrequested';
import { ViewDataPreference } from 'runtime/data/viewdatapreference';
import { WorkItemDataVersionPreference } from 'runtime/data/workitemdataversionpreference';
import { OpenReadOnlyWorkItemRequested } from 'runtime/events/openreadonlyworkitemrequested';
import { CreateWorkItemRequested } from 'runtime/events/createworkitemrequested';

import { SystemError } from 'common/exceptions/systemerror';

import { Serializer } from 'common/serializer';

import { NotificationSummaryRequested } from 'runtime/events/notificationsummaryrequested';
import { NotificationInfo } from 'runtime/data/notificationinfo';

import { standardRequest } from 'common/requestidiom';
import * as path from 'shared/path';

export class Screen implements IDisposable {
    protected eventAggregator: IEventAggregator;

    protected $element: JQuery;

    protected resources: IResourceProvider;
    protected resourcesAvailable: AsyncSubject<boolean>;

    protected screenDisposable: IDisposable;

    constructor(
        private $hostElement: JQuery,
        resourcesObservable: Observable<IResourceProvider>,
        private parentEventAggregator: IEventAggregator,
        protected serializer: Serializer,
        protected csrfToken: () => string
    ) {
        const events = new EventAggregator();
        events.passthrough(parentEventAggregator, RuntimeInfoRequested);
        this.eventAggregator = events;
        this.resourcesAvailable = new AsyncSubject<boolean>();

        this.screenDisposable = disposableSubscription(
            this.eventAggregator.query(Busy).subscribe(x => parentEventAggregator.publish(x)),
            this.eventAggregator.query(NotBusy).subscribe(x => parentEventAggregator.publish(x)),
            this.eventAggregator.query(DialogRequested).subscribe(x => parentEventAggregator.publish(x)),
            //this.eventAggregator.query(RequestError).subscribe(x => parentEventAggregator.publish(x)),
            this.eventAggregator.query(SystemErrorMessage).subscribe(x => parentEventAggregator.publish(x)),
            //this.eventAggregator.query(LoadError).subscribe(value => new ErrorReport(this.resources, 'Screen Load Error').addError({ message: value.exception })),

            parentEventAggregator.query(RuntimeHostClosed).subscribe(event => {
                this.$element.show();
                this.eventAggregator.publish(event);
            }),

            resourcesObservable.subscribe(
                value => {
                    this.resources = value;
                    this.resourcesAvailable.next(true);
                    this.resourcesAvailable.complete();
                },
                exception => this.resourcesAvailable.error(exception)
            ),

            this.eventAggregator.queryRequest(NotificationSummaryRequested).subscribe(x => {
                standardRequest(serializer, x, path.api(`notification/summary/${x.payload.workItemId}`))
                    .handleArray(NotificationInfo, r => x.success(r))
                    .get(x.cancel)
            })
        );
    }

    dispose(): void {}

    protected httpError(message: string, textStatus: string): void {
        //this.resourcesAvailable.subscribe(() => this.eventAggregator.publish(new RequestError(
        //    this.resources.getCoreString(textStatus === 'timeout' ? 'RequestTimeout' : 'NetworkError'))
        //));
    }

    protected systemError(t: SystemError): void {
        //this.eventAggregator.publish(new SystemErrorMessage(t));
    }

    protected exception(exception: any): void {
        if (exception instanceof TypeError) {
            var typeEr = new ErrorReport(this.resources, 'Type Error');
            typeEr.addError({ message: (<TypeError>exception).message });
        } else {
            var er = new ErrorReport(this.resources);
            er.addError({ message: exception });
        }
    }

    // this is a placeholder for the the actual view screens to implement
    protected pauseBackgroundProcessing(): void { }

    // this is a placeholder for the the actual view screens to implement
    protected resumeBackgroundProcessing(): void { }

    protected openEdit(workItemId: string): void {
        this.eventAggregator.publish(new Busy());
        this.pauseBackgroundProcessing();
        this.$element.hide();
        this.parentEventAggregator.publish(new OpenWritableWorkItemRequested(workItemId, null, false));
        this.eventAggregator.publish(new NotBusy());
    }

    protected openRead(workItemId: string, viewDataPreference: ViewDataPreference): void {
        var preference: WorkItemDataVersionPreference;
        switch (viewDataPreference) {
            case ViewDataPreference.prompt:
                preference = WorkItemDataVersionPreference.None;
                break;
            case ViewDataPreference.modified:
                preference = WorkItemDataVersionPreference.PreferSaved;
                break;
            case ViewDataPreference.original:
                preference = WorkItemDataVersionPreference.PreferSubmitted;
                break;
            default:
                this.exception(this.resources.getCoreString('ErrUnknownViewDataPreferenceValue'));
                break;
        }

        if (preference !== undefined && preference !== null) {
            this.eventAggregator.publish(new Busy());
            this.pauseBackgroundProcessing();
            this.$element.hide();
            this.parentEventAggregator.publish(new OpenReadOnlyWorkItemRequested(workItemId, preference, null, false));
            this.eventAggregator.publish(new NotBusy());
        }
    }

    protected create(definitionName: string): void {
        this.eventAggregator.publish(new Busy());
        this.$element.hide();
        this.parentEventAggregator.publish(new CreateWorkItemRequested(definitionName, false));
        this.eventAggregator.publish(new NotBusy());
    }
}